/**
 * @file settings-constants.ts
 *
 * Static option lists for the Settings form's select fields, mirroring WSB-v2's
 * Shop settings. Labels are plain English example strings (the admin renders the
 * `defaultMessage`, and WSB-v2 itself shows raw example labels for date/number
 * formats), so they live here as data rather than as i18n messages. Currency
 * options are a curated ISO-4217 subset (mock-backed; the real list will come
 * from the commerce API). The large email-language list lives in its own file.
 */

import type { SettingsOption } from './settings-form-types';

/** Date-format options; `value` is the PHP date-format string. */
export const DATE_FORMAT_OPTIONS: SettingsOption[] = [
  { value: 'F j, Y', label: 'March 30, 2025' },
  { value: 'Y-m-d', label: '2025-03-30' },
  { value: 'm/d/Y', label: '03/30/2025' },
  { value: 'D, M j Y', label: 'Sun, Mar 30 2025' },
];

/** Number-format options; `value` is `thousandSep|decimalSep|numDecimals`. */
export const NUMBER_FORMAT_OPTIONS: SettingsOption[] = [
  { value: ',|.|2', label: '1,234.56' },
  { value: '.|,|2', label: '1.234,56' },
  { value: ' |,|2', label: '1 234,56' },
  { value: "'|.|2", label: "1'234.56" },
  { value: ',|.|0', label: '1,234' },
  { value: '.|,|0', label: '1.234' },
  { value: '|.|2', label: '1234.56' },
];

/** Where the currency symbol sits relative to the amount. */
export const CURRENCY_POSITION_OPTIONS: SettingsOption[] = [
  { value: 'left', label: 'Left' },
  { value: 'left_space', label: 'Left with space' },
  { value: 'right', label: 'Right' },
  { value: 'right_space', label: 'Right with space' },
];

/** Product weight unit. */
export const WEIGHT_UNIT_OPTIONS: SettingsOption[] = [
  { value: 'g', label: 'Grams (g)' },
  { value: 'kg', label: 'Kilograms (kg)' },
  { value: 'lbs', label: 'Pounds (lbs)' },
  { value: 'oz', label: 'Ounces (oz)' },
];

/**
 * IANA time-zone options, built from the runtime's own zone database
 * (`Intl.supportedValuesOf('timeZone')`) rather than a hand-kept list — the native
 * equivalent of webshop's `moment.tz.names()`. UTC and the browser's own zone are
 * pinned to the top (like webshop's "Local & GMT" group); the rest follow A→Z. The
 * label is the raw IANA id (e.g. `Europe/Copenhagen`), matching webshop.
 */
function buildTimezoneOptions(): SettingsOption[] {
  const supportedValuesOf = (Intl as { supportedValuesOf?: (key: 'timeZone') => string[] })
    .supportedValuesOf;
  const all = supportedValuesOf ? supportedValuesOf('timeZone') : [];
  const guessed = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const pinned = ['UTC', guessed].filter((tz, i, arr) => tz && arr.indexOf(tz) === i);
  const rest = all.filter((tz) => !pinned.includes(tz)).sort((a, b) => a.localeCompare(b));
  return [...pinned, ...rest].map((tz) => ({ value: tz, label: tz }));
}

/** All selectable IANA time zones (UTC + the browser's zone first, then the rest A→Z). */
export const TIMEZONE_OPTIONS: SettingsOption[] = buildTimezoneOptions();

/** Curated ISO-4217 currencies as `code → "Name (symbol)"`, sorted by name. */
export const CURRENCY_OPTIONS: SettingsOption[] = [
  { value: 'ARS', label: 'Argentine peso ($)' },
  { value: 'AUD', label: 'Australian dollar ($)' },
  { value: 'BRL', label: 'Brazilian real (R$)' },
  { value: 'CAD', label: 'Canadian dollar ($)' },
  { value: 'CNY', label: 'Chinese yuan (¥)' },
  { value: 'CZK', label: 'Czech koruna (Kč)' },
  { value: 'DKK', label: 'Danish krone (kr)' },
  { value: 'EUR', label: 'Euro (€)' },
  { value: 'HKD', label: 'Hong Kong dollar ($)' },
  { value: 'HUF', label: 'Hungarian forint (Ft)' },
  { value: 'INR', label: 'Indian rupee (₹)' },
  { value: 'IDR', label: 'Indonesian rupiah (Rp)' },
  { value: 'ILS', label: 'Israeli new shekel (₪)' },
  { value: 'JPY', label: 'Japanese yen (¥)' },
  { value: 'MYR', label: 'Malaysian ringgit (RM)' },
  { value: 'MXN', label: 'Mexican peso ($)' },
  { value: 'NZD', label: 'New Zealand dollar ($)' },
  { value: 'NOK', label: 'Norwegian krone (kr)' },
  { value: 'PHP', label: 'Philippine peso (₱)' },
  { value: 'PLN', label: 'Polish złoty (zł)' },
  { value: 'GBP', label: 'Pound sterling (£)' },
  { value: 'RUB', label: 'Russian ruble (₽)' },
  { value: 'SAR', label: 'Saudi riyal (﷼)' },
  { value: 'SGD', label: 'Singapore dollar ($)' },
  { value: 'ZAR', label: 'South African rand (R)' },
  { value: 'KRW', label: 'South Korean won (₩)' },
  { value: 'SEK', label: 'Swedish krona (kr)' },
  { value: 'CHF', label: 'Swiss franc (CHF)' },
  { value: 'THB', label: 'Thai baht (฿)' },
  { value: 'TRY', label: 'Turkish lira (₺)' },
  { value: 'AED', label: 'United Arab Emirates dirham (د.إ)' },
  { value: 'USD', label: 'United States dollar ($)' },
];
